import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box, Breadcrumbs, Tooltip, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { Formik, Form } from "formik";
import Grid from '@mui/material/Grid2';
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import TextField from '../../../components/TextField';
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import Dropdown from '../../../components/Dropdown'
import { successMessage, errorMessage } from "../../../components/Toaster";
import { getSectionsByCompanyID, createSection, updateSection, deleteSection, getSectionTypes } from "./api";
import { ApiResponse, SectionData } from "./interface";
import { getErrorMessage } from "../../../utils/error";

const Section: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const company_name = location.state?.company_name;
    const { company_id } = useParams<{ company_id: string }>();
    const [sections, setSections] = useState<SectionData[]>([]);
    const [sectionTypes, setSectionTypes] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editSection, setEditSection] = useState<SectionData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        sectionName: Yup.string().required("Section name is required"),
        sectionType: Yup.string().required("Section type is required")
    });

    const columns: GridColDef[] = [
        { field: "section_name", headerName: "Section Name", flex: 1 },
        { field: "section_type", headerName: "Section Type", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <FormatLineSpacingIcon color="action" />,
                            tooltip: "Lines",
                            onClick: () => handleSectionLines(params),
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        if (company_id) {
            const fetchSections = async () => {
                setLoading((prev) => ({ ...prev, fetch: true }));
                try {
                    const sectionsData: ApiResponse<SectionData[]> = await getSectionsByCompanyID(company_id);
                    if (sectionsData.status && sectionsData.data) {
                        setSections(sectionsData.data);
                    } else {
                        errorMessage(sectionsData.message || "Failed to fetch sections.");
                    }
                } catch (error) {
                    console.error("Error fetching sections:", error);
                    const message = getErrorMessage(error);
                    errorMessage(message || "Error fetching sections");
                } finally {
                    setLoading((prev) => ({ ...prev, fetch: false }));
                }
            };

            fetchSections();
        }
    }, [company_id]);

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: true }));

        try {
            const sectionTypesResponse: ApiResponse<string[]> = await getSectionTypes();
            if (sectionTypesResponse.status && sectionTypesResponse?.data) {
                setSectionTypes(sectionTypesResponse?.data);
            } else {
                errorMessage(sectionTypesResponse.message || "Failed to fetch section types.");
            }
        } catch (error) {
            console.error("Error fetching section types:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "Error fetching section types");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };


    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditSection(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditSection(params.row as SectionData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleSectionLines = (params: GridCellParams) => {
        navigate(`/company/${company_id}/sections/${params.row.section_id}/section_lines`, {
            state: { section_name: params.row.section_name, company_name: company_name },
        });
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        if (editSection) {
            await handleUpdate(values);
        } else {
            await handleCreate(values);
        }
        closeDialog(resetForm);
    };

    const handleCreate = async (values: { sectionName: string, sectionType: string }) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newSection = {
                company_id: company_id || "",
                section_name: values.sectionName,
                section_type: values.sectionType,
            };

            const response = await createSection(newSection);

            if (response?.status) {
                setSections((prevSections) => {
                    const currentSections = Array.isArray(prevSections) ? prevSections : [];
                    return [
                        ...currentSections,
                        {
                            section_id: response?.data?.section_id,
                            section_name: values.sectionName,
                            section_type: values.sectionType,
                            company_id: company_id || "",
                        },
                    ];
                });
                successMessage(response.message || "Section created successfully!");
            } else {
                errorMessage(response?.message || "Failed to create section.");
            }
        } catch (error) {
            console.error("Error creating section:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: { sectionName: string, sectionType: string }) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedSection = {
                section_id: editSection!.section_id,
                section_name: values.sectionName,
                section_type: values.sectionType,
                company_id: company_id || "",
            };

            const response = await updateSection(updatedSection);

            if (response?.status) {
                setSections((prevSections) =>
                    prevSections.map((section) =>
                        section.section_id === updatedSection.section_id
                            ? { ...section, ...updatedSection }
                            : section
                    )
                );
                successMessage(response.message || "Section updated successfully!");
            } else {
                errorMessage(response?.message || "Failed to update section.");
            }
        } catch (error) {
            console.error("Error updating section:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));

        if (itemToDelete) {
            try {
                const response = await deleteSection(itemToDelete.row.section_id);

                if (response?.status) {
                    setSections((prevSections) =>
                        prevSections.filter(
                            (section) => section.section_id !== itemToDelete.row.section_id
                        )
                    );
                    successMessage(response.message || "Section deleted successfully!");
                } else {
                    errorMessage(response?.message || "Failed to delete section.");
                }
            } catch (error) {
                console.error("Error deleting section:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "An unexpected error occurred. Please try again.");
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };


    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "40px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/company">
                                    Company
                                </Link>
                                <Link color="primary" to="/company">{company_name}</Link>
                                <Link to={`/company/${company_id}/sections`} state={{ company_name: company_name }} style={{ color: "primary" }}>
                                    Sections
                                </Link>
                                <Tooltip title="Sections classified as Production, Warehouse, or Dispatch - key operational units of the company." arrow>
                                    <IconButton color="info">
                                        <InfoIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                            </Breadcrumbs>
                        </div>
                        <div>
                            <div className="d-flex justify-end">
                                <div className="d-flex justify-between w-100">
                                    <TextField
                                        className="w-25"
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            style={{ height: '100%', width: '100%' }}
                            className="ag-theme-alpine m-t-20"
                        >
                            <GridTable
                                rows={sections}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.section_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                sectionName: editSection ? editSection.section_name : "",
                                sectionType: editSection ? editSection.section_type : "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue
                            }) => (
                                <>
                                    <Dialog
                                        open={isDialogOpen}
                                        onClose={() => closeDialog(resetForm)}
                                        onConfirm={handleSubmit}
                                        title={
                                            !loading.update && !loading.create
                                                ? editSection
                                                    ? "Edit Section"
                                                    : "Add New Section"
                                                : ""
                                        }
                                        confirmButtonText={
                                            !loading.update && !loading.create
                                                ? editSection
                                                    ? "Update"
                                                    : "Add"
                                                : ""
                                        }
                                        cancelButtonText={
                                            !loading.update && !loading.create ? "Cancel" : ""
                                        }
                                        icon={<WarehouseIcon />}
                                        message={
                                            <Box padding={4}>
                                                {loading.update || loading.create ? (
                                                    <Loader color="primary" size={40} />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={6}>
                                                                <TextField
                                                                    id="sectionName"
                                                                    name="sectionName"
                                                                    label="Section Name"
                                                                    required
                                                                    value={values.sectionName}
                                                                    onChange={handleChange}
                                                                    error={
                                                                        touched.sectionName &&
                                                                        Boolean(errors.sectionName)
                                                                    }
                                                                    helperText={
                                                                        touched.sectionName &&
                                                                        errors.sectionName
                                                                    }
                                                                />
                                                            </Grid>
                                                            <Grid size={6}>
                                                                {sectionTypes?.length > 0 && (
                                                                    <Dropdown
                                                                        options={sectionTypes}
                                                                        value={values.sectionType}
                                                                        onChange={(value) => setFieldValue("sectionType", value)}
                                                                        label="Section Type"
                                                                        required
                                                                        width="200px"
                                                                        error={Boolean(touched.sectionType && errors.sectionType)}
                                                                        helperText={touched.sectionType && errors.sectionType ? String(errors.sectionType) : undefined}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Form>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Section" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader color="primary" size={40} />
                                    ) : (
                                        "Are you sure you want to delete this section?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );
};

export default Section;



